import React from "react";
import {
	Box,
	Checkbox,
	Stack,
	Button,
	Dialog,
	DialogContentText,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	CircularProgress,
	Tabs,
	Tab,
	IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import { default as DataGrid } from "../../../components/LocalizedDataGrid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { v4 as uuidv4 } from "uuid";

import ConstraintsEditor from "./ConstraintsEditor";

import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../App";

export default function BulkConstraintEditor (props) {
	const STATUS_VOID = "void";
	const STATUS_WAITING = "waiting";
	const STATUS_OKAY = "okay";
	const STATUS_ERROR = "error";

	const {  points, controllers, onChange, facility } = props;

	const { t } = useTranslation();

	const [rows, setRows] = React.useState([]);
	const [constraintsTemplates, setConstraintsTemplates] = React.useState([]);
	const [constraintsTemplateID, setConstraintsTemplateID] = React.useState("");
	const [filterModel, setFilterModel] = React.useState({ items: [] });
	const [sortModel, setSortModel] = React.useState([]);
	const [activeTab, setActiveTab] = React.useState("constraints");

	const [errorMessages, setErrorMessages] = React.useState([]);

	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");

	const myRef = React.useRef();

	const {pointsApi, constraintsApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		constraintsApi.list(
			facility,
			(data) => {
				let data_copy = [...data];
				for (var i = 0; i < data_copy.length; i++) {
					data_copy[i].must_be_set_to_constraints = data_copy[
						i
					].must_be_set_to_constraints.map((item) => ({
						...item,
						id: uuidv4(),
					}));
					if (data_copy[i].minimum_utilization_constraints)
						data_copy[i].minimum_utilization_constraints = data_copy[
							i
						].minimum_utilization_constraints.map((item) => ({
							...item,
							id: uuidv4(),
						}));
					if (data_copy[i].temperature_constraints)
						data_copy[i].temperature_constraints = data_copy[
							i
						].temperature_constraints.map((item) => ({
							...item,
							id: uuidv4(),
						}));
				}
				setConstraintsTemplates(data_copy);
				if (data.length > 0) setConstraintsTemplateID(data[0].url);
				else setConstraintsTemplateID("");
			},
			() => {
				setAlertMessage(t("Couldn't connect to server."));
				setAlertOpen(true);
			}
		);
	}, [facility]);

	React.useEffect(() => {
		const category = constraintsTemplateID
			? constraintsTemplates.find((item) => item.url === constraintsTemplateID)
					?.category
			: "HVAC";

		let data_with_id = points.map((dict) => {
			let with_id_url = { ...dict, id: dict.url };
			delete with_id_url.url;
			return with_id_url;
		});

		setRows(
			data_with_id
				.filter((item) => item.category === category)
				.map((item) => ({
					...item,
					shouldCopy: false,
					status: STATUS_VOID,
				}))
		);
	}, [points, constraintsTemplateID, controllers]);

	const getControllerName = (controller_id) => {
		const controller = controllers.filter((item) => item.url === controller_id);
		if (controller.length > 0) return controller[0].name;
		return "";
	};

	const columnsTarget = [
		{
			field: "shouldCopy",
			headerName: t("Apply to?"),
			minWidth: 80,
			renderCell: (params) => {
				return (
					<Checkbox
						checked={params.value}
						onChange={() => {
							setRows(
								rows.map((item) =>
									item.id === params.id
										? { ...item, shouldCopy: !item.shouldCopy }
										: item
								)
							);
						}}
					/>
				);
			},
		},
		{
			field: "controller_name",
			headerName: t("Controller"),
			flex: 1,
			minWidth: 80,
			valueGetter: (params) => getControllerName(params.row.controller),
		},
		{ field: "identifier", headerName: t("ID"), flex: 1, minWidth: 200 },
		{ field: "location", headerName: t("Location"), flex: 1, minWidth: 200 },
		{ field: "system", headerName: t("System"), flex: 1, minWidth: 180 },
		{ field: "name", headerName: t("Device"), flex: 1, minWidth: 180 },
		{
			field: "status",
			headerName: "",
			flex: 1,
			minWidth: 50,
			renderCell: (params) => {
				return params.value === STATUS_WAITING ? (
					<CircularProgress />
				) : params.value === STATUS_OKAY ? (
					<CheckIcon />
				) : params.value === STATUS_ERROR ? (
					<ErrorIcon />
				) : (
					<></>
				);
			},
		},
	];

	const handleCopy = () => {
		const constraintsData = myRef.current.getConstraints();

		setRows((oldRows) =>
			oldRows
				.map((item) =>
					item.shouldCopy
						? { ...item, status: STATUS_WAITING }
						: { ...item, status: STATUS_VOID }
				)
				.sort((a, b) => b.shouldCopy - a.shouldCopy)
		);

		setFilterModel({ items: [] });
		setSortModel();
		setErrorMessages([]);

		const onSuccess = (data) => {
			setRows((oldRows) =>
				oldRows.map((item) =>
					item.id === data.url ? { ...item, status: STATUS_OKAY } : item
				)
			);
		};

		const onError = (id, response) => {
			setErrorMessages((em) => [...em, { id: id, messages: response }]);

			setRows((oldRows) =>
				oldRows.map((item) =>
					item.id === id ? { ...item, status: STATUS_ERROR } : item
				)
			);
		};

		rows
			.filter((item) => item.shouldCopy)
			.forEach((row) => {
				const newData = {
					...row,
					must_be_set_to_constraints: constraintsData.mustBeSetTo,
					minimum_utilization_constraints: constraintsData.minUtil,
					temperature_constraints: constraintsData.temperature,
				};

				let payload = { ...newData };
				delete payload.id;
				if (payload.status) delete payload.status;

				pointsApi.update(newData.id, payload, onSuccess, (response) =>
					onError(newData.id, response)
				);
			});

		//notify change to parent
		if (onChange) onChange();
	};

	const handleSaveTemplate = () => {
		const c = window.confirm(t("Update current template from calendar?"));

		if (c) {
			const constraintsData = getCurrentConstraints();

			let payload = { ...constraintsData };
			delete payload.id;

			const onSuccess = (data) => {
				setConstraintsTemplates((oldRows) =>
					oldRows.map((item) => (item.url === data.url ? constraintsData : item))
				);

				alert(t("Template updated successfully."));
			};

			const onError = (response) => {
				setErrorMessages((em) => [
					...em,
					{ id: "template", messages: response },
				]);
			};

			constraintsApi.update(
				constraintsData.url,
				payload,
				onSuccess,
				(response) => onError
			);
		}
	};

	const handleNewTemplate = () => {
		// todo: ask which type of point it is (binary or hvac). now just using hvac

		const name = prompt(t("Add new template. Name?"), t("Name"));

		if (name) {
			const data = {
				must_be_set_to_constraints: [],
				minimum_utilization_constraints: null,
				temperature_constraints: [],
				category: "HVAC",
				name: name,
				facility: facility,
			};

			const onSuccess = (data) => {
				setConstraintsTemplates((old) => [...old, { ...data, url: data.url }]);

				setConstraintsTemplateID(data.url);
			};

			const onError = (response) => {
				alert(t("Problem creating template."));
			};

			constraintsApi.create(data, onSuccess, onError);
		}
	};

	const handleDeleteTemplate = () => {
		const c = window.confirm(t("Delete template?"));

		if (c) {
			const deleteId = constraintsTemplateID;

			const onSuccess = () => {
				const newData = constraintsTemplates.filter(
					(item) => item.url !== deleteId
				);

				setConstraintsTemplates(newData);
				if (newData.length > 0) setConstraintsTemplateID(newData[0].url);
				else setConstraintsTemplateID("");
			};

			const onError = (response) => {
				alert(t("Problem deleting template."));
			};

			constraintsApi.delete(deleteId, onSuccess, onError);
		}
	};

	const getCurrentConstraints = () => {
		return constraintsTemplates.find(
			(item) => item.url == constraintsTemplateID
		);
	};

	const renderErrorMessages = (em) => {
		const errorMessages = em.messages;

		let errorHeader;
		if (em.id === "template") errorHeader = t("Errors in template:");
		else {
			const row = rows.find((item) => item.id === em.id);
			errorHeader =
				t("Errors in") +
				" " +
				getControllerName(row.controller) +
				" " +
				row.identifier +
				" (" +
				row.location +
				" " +
				row.system +
				" " +
				row.name +
				"):";
		}

		let dayName = {
			1: t("Monday"),
			2: t("Tuesday"),
			3: t("Wednesday"),
			4: t("Thursday"),
			5: t("Friday"),
			6: t("Saturday"),
			7: t("Sunday"),
		};

		const cc = getCurrentConstraints();

		return (
			<div key={em.id}>
				<Typography variant={"h6"}>{errorHeader}</Typography>
				{errorMessages["must_be_set_to_constraints"]
					? Object.entries(errorMessages["must_be_set_to_constraints"]).map(
							([key, value]) => (
								<Typography
									sx={{ color: "red" }}
									key={key}
								>
									- Error in constraint{" "}
									{dayName[
										cc.must_be_set_to_constraints[parseInt(key)]?.iso_week_day
									] +
										" " +
										cc.must_be_set_to_constraints[parseInt(key)]?.start +
										"-" +
										cc.must_be_set_to_constraints[parseInt(key)]?.end}
									.{" "}
									{Object.entries(value).map(([key2, value2]) =>
										key2 === "non_field_errors"
											? `${value2}`
											: `${key2}: ${value2} `
									)}
								</Typography>
							)
						)
					: null}
				{errorMessages["temperature_constraints"]
					? Object.entries(errorMessages["temperature_constraints"]).map(
							([key, value]) => (
								<Typography
									sx={{ color: "red" }}
									key={key}
								>
									Error in constraint{" "}
									{dayName[
										cc.temperature_constraints[parseInt(key)]?.iso_week_day
									] +
										" " +
										cc.temperature_constraints[parseInt(key)]?.start +
										"-" +
										cc.temperature_constraints[parseInt(key)]?.end}
									.{" "}
									{Object.entries(value).map(([key2, value2]) =>
										key2 === "non_field_errors"
											? `${value2}`
											: `${key2}: ${value2} `
									)}
								</Typography>
							)
						)
					: null}
				{errorMessages["minimum_utilization_constraints"]
					? Object.entries(
							errorMessages["minimum_utilization_constraints"]
						).map(([key, value]) => (
							<Typography
								sx={{ color: "red" }}
								key={key}
							>
								Error in constraint{" "}
								{dayName[
									cc.minimum_utilization_constraints[parseInt(key)]
										?.iso_week_day
								] +
									" " +
									cc.minimum_utilization_constraints[parseInt(key)]?.start +
									"-" +
									cc.minimum_utilization_constraints[parseInt(key)]?.end}
								.{" "}
								{Object.entries(value).map(([key2, value2]) =>
									key2 === "non_field_errors"
										? `${value2}`
										: `${key2}: ${value2} `
								)}
							</Typography>
						))
					: null}
			</div>
		);
	};

	const handleSelectTemplate = (value) => {
		setConstraintsTemplateID(value);
		setErrorMessages([]);
	};

	function numberRange(start, end) {
		return new Array(end - start).fill().map((d, i) => i + start);
	}

	const handleConstraintsChange = (event) => {
		setConstraintsTemplates((templates) =>
			templates.map((t) =>
				t.url === constraintsTemplateID
					? {
							...t,
							minimum_utilization_constraints: event.minUtil,
							must_be_set_to_constraints: event.mustBeSetTo,
							temperature_constraints: event.temperature,
						}
					: t
			)
		);
	};

	return (
		<>
			<Stack height="100%">
				<Box sx={{ borderBottom: 1, marginBottom: 3, borderColor: "divider" }}>
					<Tabs
						value={activeTab}
						onChange={(event, newValue) => setActiveTab(newValue)}
					>
						<Tab
							label={t("Step 1 - Set constraints")}
							value="constraints"
						/>
						<Tab
							label={t("Step 2 - Apply constraints")}
							value="target"
						/>
					</Tabs>
				</Box>
				<Box
					height="100%"
					sx={{ display: activeTab === "constraints" ? "block" : "none" }}
				>
					<Stack
						height="100%"
						spacing={2}
					>
						<Stack direction="row">
							<FormControl style={{ minWidth: 300 }}>
								<InputLabel>{t("Template")}</InputLabel>
								<Select
									value={constraintsTemplateID}
									label={t("Template")}
									onChange={(event) => handleSelectTemplate(event.target.value)}
								>
									{constraintsTemplates.map((item) => (
										<MenuItem
											value={item.url}
											key={item.url}
										>
											{item.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<Stack direction="row">
								<IconButton onClick={handleNewTemplate}>
									<AddIcon fontSize="small" />
								</IconButton>
								<IconButton onClick={handleSaveTemplate}>
									<SaveIcon fontSize="small" />
								</IconButton>
								<IconButton onClick={handleDeleteTemplate}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Stack>
						</Stack>
						<ConstraintsEditor
							height="100%"
							setPoints={numberRange(1, 31).join(",")}
							category={
								constraintsTemplates.find(
									(item) => item.url === constraintsTemplateID
								)?.category ?? "HVAC"
							}
							mustBeSetTo={
								constraintsTemplates.find(
									(item) => item.url === constraintsTemplateID
								)?.must_be_set_to_constraints ?? []
							}
							minUtil={
								constraintsTemplates.find(
									(item) => item.url === constraintsTemplateID
								)?.minimum_utilization_constraints ?? []
							}
							temperatureConstr={
								constraintsTemplates.find(
									(item) => item.url === constraintsTemplateID
								)?.temperature_constraints ?? []
							}
							errorMessages={{}}
							ref={myRef}
							onChange={handleConstraintsChange}
						/>
					</Stack>
				</Box>

				<Box
					sx={{
						height: "100%",
						display: activeTab === "target" ? "block" : "none",
					}}
				>
					<Stack height="100%">
						<DataGrid
							rows={rows}
							columns={columnsTarget}
							filterModel={filterModel}
							sortModel={sortModel}
							onSortModelChange={(newModel) => setSortModel(newModel)}
							onFilterModelChange={(newModel) => setFilterModel(newModel)}
						/>
						<DialogActions>
							<Button onClick={handleCopy}>Apply</Button>
						</DialogActions>
					</Stack>
				</Box>
				<Box>
					{errorMessages.map((em) => {
						return <>{renderErrorMessages(em)}</>;
					})}
				</Box>
			</Stack>
			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Problem")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
